import React, {useMemo, useState} from "react";
import "../styles/auth-page.css";
import {BASE_URLS} from "../config/urls";
import {useAuth} from "../context/AuthContext";
import {useDocumentLinks} from "../context/DocLinksContext";
import {BtnSpinner} from "../components/fields/BtnSpinner";

type Mode = "login" | "registration";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const passwordLengthRegex = /^.{6,}$/;
const passwordLettersRegex = /[A-Za-z]/;
const nameAllowedRegex = /^[A-Za-zА-Яа-яЁё\s-]+$/;

function isValidName(name: string) {
    const s = name.trim();
    if (!s) return false;
    // хотя бы одна буква
    if (!/[A-Za-zА-Яа-яЁё]/.test(s)) return false;
    // только разрешённые символы
    return nameAllowedRegex.test(s);
}

export default function AuthPage({mode}: { mode: Mode }) {
    const isRegistration = mode === "registration";
    const {login} = useAuth();
    const docLinks = useDocumentLinks();

    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [agree, setAgree] = useState(false);

    const [emailError, setEmailError] = useState("");
    const [nameError, setNameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmError, setConfirmError] = useState("");
    const [agreeError, setAgreeError] = useState("");
    const [formError, setFormError] = useState("");

    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const title = isRegistration ? "Регистрация" : "Вход";
    const buttonText = isRegistration ? "Зарегистрироваться" : "Войти";

    const endpoint = useMemo(() => {
        return isRegistration ? `${BASE_URLS.api}/registration` : `${BASE_URLS.api}/login`;
    }, [isRegistration]);

    const clearErrors = () => {
        setEmailError("");
        setNameError("");
        setPasswordError("");
        setConfirmError("");
        setAgreeError("");
        setFormError("");
    };

    const validate = () => {
        let ok = true;
        clearErrors();

        const e = email.trim().toLowerCase();
        if (!emailRegex.test(e)) {
            setEmailError("Неправильная электронная почта");
            ok = false;
        }

        if (isRegistration) {
            const n = name.trim();
            if (!isValidName(n)) {
                setNameError("Введите имя (буквы, пробелы или дефис)");
                ok = false;
            }
            if (!agree) {
                setAgreeError("Необходимо согласиться с политикой конфиденциальности");
                ok = false;
            }
        }

        if (!passwordLettersRegex.test(password) || !passwordLengthRegex.test(password)) {
            setPasswordError("Пароль: минимум 6 символов и хотя бы 1 латинская буква");
            ok = false;
        }

        if (isRegistration && confirmPassword !== password) {
            setConfirmError("Пароли не совпадают");
            ok = false;
        }

        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;

        if (!validate()) return;

        try {
            setLoading(true);

            const payload: any = {
                email: email.trim().toLowerCase(),
                password,
            };

            if (isRegistration) payload.name = name.trim();

            const res = await fetch(endpoint, {
                method: "POST",
                headers: {"Content-Type": "application/json", Accept: "application/json"},
                credentials: "include",
                body: JSON.stringify(payload),
            });

            let data: any = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            if (!res.ok) {
                const msg = data?.message || data?.error || "Ошибка. Проверьте данные.";
                setEmailError(msg);
                return;
            }

            const roles: any[] = data?.roles || [];
            login(roles);

            window.location.assign(`${BASE_URLS.app}/personal-account`);
        } catch (err) {
            console.error(err);
            setFormError("Произошла ошибка при отправке запроса. Попробуйте позже.");
        } finally {
            setLoading(false);
        }
    };

    const goRegistration = () => window.location.assign(`${BASE_URLS.app}/registration`);
    const goLogin = () => window.location.assign(`${BASE_URLS.app}/login`);

    const forgotPassword = () => {
        // если у тебя есть отдельная страница — замени
        window.location.assign(`${BASE_URLS.app}/recover-password`);
    };

    return (
        <div className="auth-page">
            <div className="auth-wrap">
                <div className="auth-card card">
                    <div className="auth-head">
                        <h1 className="auth-title">{title}</h1>
                    </div>

                    {formError && <div className="auth-alert auth-alert--error">{formError}</div>}

                    <form className="auth-form" onSubmit={handleSubmit}>
                        {isRegistration && (
                            <div className="auth-field">
                                <div className="auth-label">Имя</div>
                                <input
                                    className={`auth-input ${nameError ? "is-error" : ""}`}
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setNameError("");
                                        setFormError("");
                                    }}
                                    placeholder="Например: Иван"
                                    autoComplete="name"
                                />
                                {nameError && <div className="auth-error">{nameError}</div>}
                            </div>
                        )}

                        <div className="auth-field">
                            <div className="auth-label">Email</div>
                            <input
                                className={`auth-input ${emailError ? "is-error" : ""}`}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                    setFormError("");
                                }}
                                placeholder="name@mail.com"
                                inputMode="email"
                                autoComplete="email"
                            />
                            {emailError && <div className="auth-error">{emailError}</div>}
                        </div>

                        <div className="auth-field">
                            <div className="auth-label">Пароль</div>

                            <div className="auth-pass-row">
                                <input
                                    className={`auth-input auth-input--pass ${passwordError ? "is-error" : ""}`}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError("");
                                        setConfirmError("");
                                        setFormError("");
                                    }}
                                    placeholder="Минимум 6 символов"
                                    type={showPass ? "text" : "password"}
                                    autoComplete={isRegistration ? "new-password" : "current-password"}
                                />

                                <button
                                    type="button"
                                    className="auth-eye"
                                    onClick={() => setShowPass((p) => !p)}
                                    aria-label={showPass ? "Скрыть пароль" : "Показать пароль"}
                                    title={showPass ? "Скрыть" : "Показать"}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden
                                    >
                                        {showPass ? (
                                            <>
                                                <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12" stroke="#000000"
                                                      stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <path d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12" stroke="#000000"
                                                      stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <circle cx="12" cy="12" r="3" stroke="#000000"
                                                        stroke-linecap="round" stroke-linejoin="round"></circle>
                                            </>
                                        ) : (
                                            <>
                                                <path d="M2 2L22 22" stroke="#000000" stroke-width="1"
                                                      stroke-linecap="round" stroke-linejoin="round"></path>
                                                <path
                                                    d="M6.71277 6.7226C3.66479 8.79527 2 12 2 12C2 12 5.63636 19 12 19C14.0503 19 15.8174 18.2734 17.2711 17.2884M11 5.05822C11.3254 5.02013 11.6588 5 12 5C18.3636 5 22 12 22 12C22 12 21.3082 13.3317 20 14.8335"
                                                    stroke="#000000" stroke-width="1" stroke-linecap="round"
                                                    stroke-linejoin="round"></path>
                                                <path
                                                    d="M14 14.2362C13.4692 14.7112 12.7684 15.0001 12 15.0001C10.3431 15.0001 9 13.657 9 12.0001C9 11.1764 9.33193 10.4303 9.86932 9.88818"
                                                    stroke="#000000" stroke-width="1" stroke-linecap="round"
                                                    stroke-linejoin="round"></path>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {passwordError && <div className="auth-error">{passwordError}</div>}
                        </div>

                        {isRegistration && (
                            <div className="auth-field">
                                <div className="auth-label">Подтверждение пароля</div>
                                <input
                                    className={`auth-input ${confirmError ? "is-error" : ""}`}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setConfirmError("");
                                        setFormError("");
                                    }}
                                    placeholder="Повторите пароль"
                                    type={showPass ? "text" : "password"}
                                    autoComplete="new-password"
                                />
                                {confirmError && <div className="auth-error">{confirmError}</div>}
                            </div>
                        )}

                        {isRegistration && (
                            <div className="auth-agree">
                                <label className={`auth-check ${agreeError ? "is-error" : ""}`}>
                                    <input
                                        type="checkbox"
                                        checked={agree}
                                        onChange={(e) => {
                                            setAgree(e.target.checked);
                                            setAgreeError("");
                                            setFormError("");
                                        }}
                                    />
                                    <span className="auth-check-ui"/>
                                    <span className="auth-check-text">
                    Я согласен с{" "}
                                        <span
                                            className="auth-link"
                                            onClick={() => window.open(docLinks.privacy, "_blank", "noopener,noreferrer")}
                                            role="button"
                                            tabIndex={0}
                                        >
                      политикой конфиденциальности
                    </span>
                  </span>
                                </label>
                                {agreeError && <div className="auth-error">{agreeError}</div>}
                            </div>
                        )}

                        {!isRegistration && (
                            <div className="auth-row-between">
                                <span/>
                                <button type="button" className="auth-ghost" onClick={forgotPassword}>
                                    Забыли пароль?
                                </button>
                            </div>
                        )}

                        <button className="auth-submit btn-primary" type="submit" disabled={loading}>
                            {loading ? <BtnSpinner/> : buttonText}
                        </button>

                        <div className="auth-support">
                            * Чтобы получить доступ к админ-панели, обратитесь в{" "}
                            <a
                                href="https://t.me/zayaa_iz_rayaa"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="auth-link"
                            >
                                поддержку
                            </a>
                        </div>

                        <div className="auth-foot">
                            {isRegistration ? (
                                <span>
                  Уже есть аккаунт?{" "}
                                    <span className="auth-link" onClick={goLogin}>
                    Войти
                  </span>
                </span>
                            ) : (
                                <span>
                  Нет аккаунта?{" "}
                                    <span className="auth-link" onClick={goRegistration}>
                    Зарегистрироваться
                  </span>
                </span>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
